import React, { useEffect, useState } from 'react';
import {
  Accordion,
  Button,
  Card,
  Collapse,
  Container,
  Table,
} from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import DatePicker from 'react-datepicker';
import { fi } from 'date-fns/locale';
import * as signalR from '@microsoft/signalr';
import { format } from 'date-fns';
import {
  addStatusToast,
  InputCheck,
  RevertConfirmation,
  SectionHeader,
} from '../../common/components';
import { TagSelector } from '../../common/components/TagSelector';
import { AccommodationSelector } from '../../common/components/AccommodationSelector';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  clearSeasonRateBatchUpdateDetails,
  copySeasonRatesFromAccommodationToAnother,
  copySeasonRatesToNextSalesPeriod,
  fetchLastTagOperation,
  fetchSeasonRateBatchUpdateDetails,
  type LastTagOperationDto,
  type SeasonRateNotCopiedRecordDto,
} from './rateToolsSlice';
import LastTagOperationInfo from './LastTagOperationInfo';
import signalRPricingService from '../../common/hubs/signalRPricingService';
import { formatDateToFinnish } from '../../common/utils/dateUtils';
import {
  SEASON_RATE_BATCH_OPERATION_TYPE,
  SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS,
} from '../../common/constants';

interface HistoryUpdateDetailsTableProps {
  updateDetails: {
    accommodationNumber?: number;
    accommodationName?: string;
    seasonRatesChangedCount: number;
    seasonRatesNotCopiedCount: number;
    notCopiedSeasons?: SeasonRateNotCopiedRecordDto[];
  }[];
  operationType: SEASON_RATE_BATCH_OPERATION_TYPE;
}

function HistoryUpdateDetailsTable({
  updateDetails,
  operationType,
}: HistoryUpdateDetailsTableProps) {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const toggleRow = (num: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(num)) next.delete(num);
      else next.add(num);
      return next;
    });
  };

  const isCopying =
    operationType === SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying;

  return (
    <Table striped bordered hover className="mt-2">
      <thead>
        <tr>
          <th>Kohteen numero</th>
          <th>Kohteen nimi</th>
          <th>Kausia muutettu</th>
          {isCopying && <th>Kausia ei kopioitu</th>}
        </tr>
      </thead>
      <tbody>
        {updateDetails.map(updateDetail => {
          const notCopied = updateDetail.seasonRatesNotCopiedCount ?? 0;
          const copied = updateDetail.seasonRatesChangedCount;
          let rowClass = '';
          if (isCopying && copied === 0 && notCopied > 0)
            rowClass = 'table-danger';
          else if (isCopying && notCopied > 0) rowClass = 'table-warning';
          const hasDetails =
            isCopying && (updateDetail.notCopiedSeasons?.length ?? 0) > 0;
          const key = updateDetail.accommodationNumber ?? Math.random();
          const isExpanded = expandedRows.has(Number(key));

          return (
            <React.Fragment key={key}>
              <tr
                className={rowClass}
                style={hasDetails ? { cursor: 'pointer' } : undefined}
                onClick={hasDetails ? () => toggleRow(Number(key)) : undefined}
              >
                <td>{updateDetail.accommodationNumber}</td>
                <td>
                  {updateDetail.accommodationName}
                  {hasDetails && (
                    <span className="ms-2 text-muted small">
                      {isExpanded ? '▲' : '▼'} näytä ei kopioituneet
                    </span>
                  )}
                </td>
                <td>{updateDetail.seasonRatesChangedCount}</td>
                {isCopying && <td>{notCopied}</td>}
              </tr>
              {hasDetails && (
                <tr className="p-0">
                  <td colSpan={isCopying ? 4 : 3} className="p-0">
                    <Collapse in={isExpanded}>
                      <div>
                        <Table size="sm" className="mb-0 bg-light">
                          <thead>
                            <tr className="table-secondary">
                              <th className="ps-4">Kauden nimi</th>
                              <th>Lähdekausi alkaa</th>
                              <th>Lähdekausi päättyy</th>
                              <th>Kopioitu alkamaan</th>
                              <th>Kopioitu päättymään</th>
                            </tr>
                          </thead>
                          <tbody>
                            {updateDetail.notCopiedSeasons?.map(s => (
                              <tr key={`${s.seasonName}-${s.startDate}`}>
                                <td className="ps-4">{s.seasonName}</td>
                                <td>
                                  {s.sourceStartDate
                                    ? new Date(
                                        s.sourceStartDate,
                                      ).toLocaleDateString('fi-FI')
                                    : '—'}
                                </td>
                                <td>
                                  {s.sourceEndDate
                                    ? new Date(
                                        s.sourceEndDate,
                                      ).toLocaleDateString('fi-FI')
                                    : '—'}
                                </td>
                                <td>
                                  {new Date(s.startDate).toLocaleDateString(
                                    'fi-FI',
                                  )}
                                </td>
                                <td>
                                  {new Date(s.endDate).toLocaleDateString(
                                    'fi-FI',
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Collapse>
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
    </Table>
  );
}

interface CopyMessage {
  accommodationNumber?: string;
  accommodationName?: string;
  seasonRatesChangedCount?: string;
  seasonRatesNotCopiedCount?: string;
}

interface CopySummary {
  accommodationsCopiedCount: string;
  accommodationsNotCopiedCount: string;
}

const CopySeasonRates = () => {
  const dispatch = useAppDispatch();
  const [selectedTagId, setSelectedTagId] = useState<string | null>(null);
  const [lastTagOperation, setLastTagOperation] =
    useState<LastTagOperationDto | null>(null);
  const [selectedAccommodationId, setSelectedAccommodationId] = useState<
    string | null
  >(null);
  const [selectedSourceAccommodationId, setSelectedSourceAccommodationId] =
    useState<string | null>(null);
  const [selectedDateForTag, setSelectedDateForTag] = useState<Date | null>(
    null,
  );
  const [selectedDateForAccommodation, setSelectedDateForAccommodation] =
    useState<Date | null>(null);

  const [copyPrices, setCopyPrices] = useState(true);
  const [copyRules, setCopyRules] = useState(true);
  const [useRulePrices, setUseRulePrices] = useState(false);

  const [copyMessages, setCopyMessages] = useState<CopyMessage[]>([]);
  const [copySummary, setCopySummary] = useState<CopySummary | null>(null);
  const [isCopying, setIsCopying] = useState<boolean>(false);
  const [revertModalState, setRevertModalState] = useState<{
    show: boolean;
    batchId?: string;
    operationType?: SEASON_RATE_BATCH_OPERATION_TYPE;
  }>({ show: false });
  const seasonRateBatchUpdateDetails = useAppSelector(
    state => state.rateTools.seasonRateBatchUpdateDetails,
  );

  useEffect(() => {
    dispatch(clearSeasonRateBatchUpdateDetails());
    dispatch(
      fetchSeasonRateBatchUpdateDetails(
        SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying,
      ),
    );
  }, [dispatch]);

  useEffect(() => {
    if (
      signalRPricingService.connection.state ===
      signalR.HubConnectionState.Disconnected
    ) {
      console.log('start connection');
      signalRPricingService.startConnection();
    }

    signalRPricingService.onReceiveCopyMessage = (
      accommodationNumber: string,
      accommodationName: string,
      seasonRatesChangedCount: string,
      seasonRatesNotCopiedCount: string,
    ) => {
      setCopyMessages(prevCopyMessages => [
        ...prevCopyMessages,
        {
          accommodationNumber,
          accommodationName,
          seasonRatesChangedCount,
          seasonRatesNotCopiedCount,
        },
      ]);
    };

    signalRPricingService.onReceiveCopySummary = (
      accommodationsCopiedCount: string,
      accommodationsNotCopiedCount: string,
    ) => {
      setCopySummary({
        accommodationsCopiedCount,
        accommodationsNotCopiedCount,
      });
    };

    signalRPricingService.onReceiveRevertSummary = (
      revertedCount: string,
      conflictCount: string,
    ) => {
      const conflictSuffix =
        Number(conflictCount) > 0
          ? ` (${conflictCount} konfliktia ohitettu)`
          : '';
      dispatch(
        addStatusToast({
          Message: `Palautus valmis: ${revertedCount} riviä palautettu${conflictSuffix}.`,
          IsFailed: false,
        }),
      );
      dispatch(
        fetchSeasonRateBatchUpdateDetails(
          SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying,
        ),
      );
    };

    /* TODO: Check if this is needed
    return () => {
      if (
        signalRPricingService.connection.state !==
        signalR.HubConnectionState.Disconnected
      ) {
        console.log('stop connection');
        signalRPricingService.stopConnection();
      }
    }; */
  }, []);

  const handleTagSelected = async (tagId: string) => {
    setSelectedTagId(tagId);

    if (tagId === null) {
      setSelectedDateForTag(null);
      setLastTagOperation(null);
    } else {
      const result = await dispatch(
        fetchLastTagOperation({
          tagId,
          operationType: SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying,
        }),
      );
      setLastTagOperation(
        (result.payload as LastTagOperationDto | null) ?? null,
      );
    }
  };

  const handleAccommodationSelected = (accommodationId: string) => {
    setSelectedAccommodationId(accommodationId);

    if (accommodationId === null) {
      setSelectedDateForAccommodation(null);
    }
  };

  const handleSourceAccommodationSelected = (accommodationId: string) => {
    setSelectedSourceAccommodationId(accommodationId);

    if (selectedSourceAccommodationId === null) {
      setSelectedDateForAccommodation(null);
    }
  };

  const handleSeasonRateCopyForTag = async () => {
    if (selectedTagId && selectedDateForTag) {
      setCopyMessages([]);
      setCopySummary(null);
      setIsCopying(true);
      await dispatch(
        copySeasonRatesToNextSalesPeriod({
          tagId: selectedTagId,
          copySeasonsTo: formatDateToFinnish(selectedDateForTag),
        }),
      );
      setIsCopying(false);
    }
  };

  const handleSeasonRateCopyForAccommodation = async () => {
    if (selectedAccommodationId && selectedDateForAccommodation) {
      setCopyMessages([]);
      setCopySummary(null);
      setIsCopying(true);
      await dispatch(
        copySeasonRatesToNextSalesPeriod({
          accommodationId: selectedAccommodationId,
          copySeasonsTo: formatDateToFinnish(selectedDateForAccommodation),
        }),
      );
      setIsCopying(false);
    }
  };

  const handleSeasonRateCopyForAccommodationToAnother = async () => {
    if (
      selectedAccommodationId &&
      selectedDateForAccommodation &&
      selectedSourceAccommodationId
    ) {
      setIsCopying(true);
      await dispatch(
        copySeasonRatesFromAccommodationToAnother({
          fromAccommodationId: selectedSourceAccommodationId,
          toAccommodationId: selectedAccommodationId,
          copyPrices,
          copyRules,
          useRulePrices,
          copySeasonsTo: formatDateToFinnish(selectedDateForAccommodation),
          copyOnlyFutureSeasons: true,
        }),
      );
      setIsCopying(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Hinnoittelu - Kopioi hintakausia</title>
      </Helmet>

      <Container className="container-content">
        <SectionHeader
          Breadcrumbs={['Hinnoittelu']}
          Title="Kopioi hintakausia"
        />
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <TagSelector
              filterActiveAccommodations
              Subtitle="Hae tagi jonka kohteille uusia hintakausia kopioidaan"
              Title="Valitse tagi"
              onTagSelected={handleTagSelected}
            />
            <LastTagOperationInfo lastOperation={lastTagOperation} />
            {selectedTagId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Uusien hintakausien kopioinnin viimeinen päivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedDateForTag(date)}
                  className="form-control"
                  selected={selectedDateForTag}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleSeasonRateCopyForTag}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Kopioidaan...'
                    : 'Kopioi hintakaudet tagin kohteille'}
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <AccommodationSelector
              Subtitle="Hae kohde jolle uusia hintakausia kopioidaan"
              Title="Valitse kohde"
              onAccommodationSelected={handleAccommodationSelected}
            />
            {selectedAccommodationId && (
              <>
                <p className="text-gray-600 mt-16 mb-16">
                  Uusien hintakausien kopioinnin viimeinen päivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedDateForAccommodation(date)}
                  className="form-control"
                  selected={selectedDateForAccommodation}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleSeasonRateCopyForAccommodation}
                  disabled={isCopying}
                >
                  {isCopying ? 'Kopioidaan...' : 'Kopioi hintakaudet kohteelle'}
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="my-48">
          <div
            className="border bg-white rounded shadow-lg mx-auto p-24 p-xl-32"
            style={{ maxWidth: 640 }}
          >
            <AccommodationSelector
              Subtitle="Kohde, jolle tuodaan uusia kausia toiselta kohteelta"
              Title="Valitse kohde, jolle tuodaan uudet kaudet toiselta kohteelta"
              onAccommodationSelected={handleAccommodationSelected}
            />
            {selectedAccommodationId && (
              <>
                <div style={{ marginTop: '20px', marginBottom: '20px' }}>
                  <AccommodationSelector
                    Subtitle="Kohde, jolta hintakaudet kopioidaan"
                    Title="Valitse kohde, jolta kaudet kopioidaan"
                    onAccommodationSelected={handleSourceAccommodationSelected}
                  />
                </div>
                <InputCheck
                  Id="copyPrices"
                  Name="copyPrices"
                  Label="Tuo hinnat"
                  HandleChange={e => {
                    const { checked } = e.target;
                    setCopyPrices(checked);
                    if (!checked) {
                      setUseRulePrices(false);
                    }
                  }}
                  Checked={copyPrices}
                  IsRequired={false}
                />
                <InputCheck
                  Id="useRulePrices"
                  Name="useRulePrices"
                  Label="Käytä korotettuja hintoja"
                  HandleChange={e => setUseRulePrices(e.target.checked)}
                  Checked={useRulePrices}
                  IsRequired={false}
                  IsDisabled={!copyPrices}
                />
                <InputCheck
                  Id="copyRules"
                  Name="copyRules"
                  Label="Tuo säännöt"
                  HandleChange={e => setCopyRules(e.target.checked)}
                  Checked={copyRules}
                  IsRequired={false}
                />
                <p className="text-gray-600 mt-16 mb-16">
                  Uusien hintakausien kopioinnin viimeinen päivämäärä
                </p>
                <DatePicker
                  locale={fi}
                  dateFormat="dd.MM.yyyy"
                  onChange={date => setSelectedDateForAccommodation(date)}
                  className="form-control"
                  selected={selectedDateForAccommodation}
                  showWeekNumbers
                  showYearDropdown
                  scrollableYearDropdown
                />
                <Button
                  variant="primary"
                  className="mt-16 mb-16 w-100"
                  onClick={handleSeasonRateCopyForAccommodationToAnother}
                  disabled={isCopying}
                >
                  {isCopying
                    ? 'Kopioidaan...'
                    : 'Kopioi hintakaudet toiselta kohteelta'}
                </Button>
              </>
            )}
          </div>
        </div>

        {copyMessages.length > 0 && (
          <Card className="bg-white border shadow-none">
            <h6 className="text-uppercase text-muted m-2">Kopioidut kaudet</h6>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Kohteen numero</th>
                  <th>Kohteen nimi</th>
                  <th>Kausia kopioitu</th>
                  <th>Ei kopioidut kaudet</th>
                </tr>
              </thead>
              <tbody>
                {copyMessages.map(copyMessage => {
                  const notCopied = Number(
                    copyMessage.seasonRatesNotCopiedCount ?? '0',
                  );
                  const copied = Number(
                    copyMessage.seasonRatesChangedCount ?? '0',
                  );

                  let rowClass = '';

                  if (copied === 0 && notCopied > 0) {
                    rowClass = 'table-danger';
                  } else if (notCopied > 0) {
                    rowClass = 'table-warning';
                  }

                  return (
                    <tr
                      key={`${copyMessage.accommodationNumber}`}
                      className={rowClass}
                    >
                      <td>{copyMessage.accommodationNumber}</td>
                      <td>{copyMessage.accommodationName}</td>
                      <td>{copyMessage.seasonRatesChangedCount}</td>
                      <td>{copyMessage.seasonRatesNotCopiedCount ?? '0'}</td>
                    </tr>
                  );
                })}
                {copySummary && (
                  <tr>
                    <td colSpan={2}>
                      <strong>Yhteensä</strong>
                    </td>
                    <td>
                      <strong>
                        Kohteita kopioitu:{' '}
                        {copySummary.accommodationsCopiedCount}
                      </strong>
                    </td>
                    <td>
                      <strong>
                        Kohteita ei kopioitu:{' '}
                        {copySummary.accommodationsNotCopiedCount}
                      </strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Card>
        )}

        {seasonRateBatchUpdateDetails && (
          <Accordion>
            {seasonRateBatchUpdateDetails.map((detail, index) => {
              const isRevertOperation =
                detail.operationType ===
                SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopyingRevert;
              return (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={detail.id ?? index}
                  className={isRevertOperation ? 'bg-light' : ''}
                >
                  <Accordion.Header>
                    <div className="d-flex justify-content-between align-items-start w-100 me-3">
                      <div>
                        <h6
                          className={`text-uppercase m-2 ${
                            isRevertOperation
                              ? 'text-secondary fst-italic'
                              : 'text-muted'
                          }`}
                        >
                          {isRevertOperation && '↶ '}
                          {
                            SEASON_RATE_BATCH_OPERATION_TYPE_DESCRIPTIONS[
                              detail.operationType
                            ]
                          }{' '}
                          -{' '}
                          {format(
                            new Date(detail.operationDate),
                            'dd.MM.yyyy HH:mm:ss',
                          )}
                        </h6>
                        {detail.requestParametersDescription && (
                          <div className="text-muted small ms-2 mb-1">
                            {detail.requestParametersDescription}
                          </div>
                        )}
                      </div>
                      {detail.isRevertible && (
                        <Button
                          variant="outline-warning"
                          size="sm"
                          onClick={e => {
                            e.stopPropagation();
                            setRevertModalState({
                              show: true,
                              batchId: detail.id,
                              operationType: detail.operationType,
                            });
                          }}
                        >
                          Palauta
                        </Button>
                      )}
                    </div>
                  </Accordion.Header>
                  <Accordion.Body>
                    <Card className="bg-white border shadow-none p-24 p-xl-32">
                      <strong>Valitut parametrit:</strong>{' '}
                      {detail.requestParametersDescription}
                      {detail.operationType ===
                        SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying && (
                        <p className="mt-2 mb-0">
                          <strong>Kohteita kopioitu:</strong>{' '}
                          {detail.accommodationsCopiedCount ?? 0}
                          {' — '}
                          <strong>Kohteita ei kopioitu:</strong>{' '}
                          {detail.accommodationsNotCopiedCount ?? 0}
                        </p>
                      )}
                      {detail.updateDetails &&
                        detail.updateDetails.length > 0 && (
                          <HistoryUpdateDetailsTable
                            updateDetails={detail.updateDetails}
                            operationType={detail.operationType}
                          />
                        )}
                    </Card>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
          </Accordion>
        )}

        {revertModalState.show &&
          revertModalState.batchId &&
          revertModalState.operationType && (
            <RevertConfirmation
              ShowModal={revertModalState.show}
              BatchUpdateDetailId={revertModalState.batchId}
              OperationType={revertModalState.operationType}
              HideModal={() => setRevertModalState({ show: false })}
              OnReverted={() => {
                dispatch(
                  fetchSeasonRateBatchUpdateDetails(
                    SEASON_RATE_BATCH_OPERATION_TYPE.SeasonRateCopying,
                  ),
                );
              }}
            />
          )}
      </Container>
    </>
  );
};

export default CopySeasonRates;
